import type { DuplicateKind, HideReason } from '../api/client.js';
import { day } from '../format.js';

/**
 * The duplicate-SKU readings the Duplicate products page and the product page's
 * lineage share: which duplicate set an ASIN is in, the plan's hide verdict with
 * its reason, whether a SKU's history is captured, and the safe-to-remove reading.
 */

export const KIND_LABEL: Record<DuplicateKind, string> = {
  multiple_active: 'Two or more Active',
  active_plus_inactive: 'Active + non-Active',
};

/** A short why for each hide reason. */
export const HIDE_REASON_LABEL: Record<HideReason, string> = {
  unique_asin: 'Only SKU on the ASIN',
  override: 'Quantity override',
  has_stock: 'Holds stock',
  active_kept: 'Active, no stocked Active twin',
  no_stocked_sibling: 'No stocked SKU on the ASIN',
  superseded: 'Superseded empty duplicate',
};

/** Which set the ASIN falls in. */
export function KindBadge({ kind }: { kind: DuplicateKind }) {
  const isMultiple = kind === 'multiple_active';
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
        isMultiple
          ? 'bg-ok-bg text-ok-text ring-ok-line'
          : 'bg-warn-bg text-warn-text ring-warn-line'
      }`}
    >
      {KIND_LABEL[kind]}
    </span>
  );
}

/** The plan's hide verdict for one SKU: shown or hidden, with the reason. */
export function VerdictBadge({ hidden, reason }: { hidden: boolean; reason: HideReason }) {
  const label = HIDE_REASON_LABEL[reason];
  return (
    <span className="inline-flex items-center gap-1.5" title={label}>
      <span
        className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
          hidden ? 'bg-warn-bg text-warn-text ring-warn-line' : 'text-muted ring-line'
        }`}
      >
        {hidden ? 'Hidden' : 'Shown'}
      </span>
      <span className="text-xs text-muted">{label}</span>
    </span>
  );
}

/** Whether a SKU's sales history is captured, or what to check first. */
export function HistoryReading({ captured, reason }: { captured: boolean; reason: string | null }) {
  return (
    <span
      className={captured ? 'text-ok-text' : 'text-warn-text'}
      title={
        captured
          ? 'First sale after the import began and last sale inside both coverages'
          : `History may be lost (${reason ?? 'check first'})`
      }
    >
      {captured ? 'Captured' : `Check first${reason ? ` · ${reason}` : ''}`}
    </span>
  );
}

/** Already removed on Amazon, ready to remove, or nothing to do. */
export function RemovalReading({
  delistedOn,
  readyToRemove,
}: {
  delistedOn: string | null;
  readyToRemove: boolean;
}) {
  if (delistedOn !== null) {
    return (
      <span
        className="text-muted"
        title={`The All Listings import of ${day(delistedOn)} no longer carries this SKU: it is already removed in Seller Central`}
      >
        Already removed on Amazon
      </span>
    );
  }
  if (readyToRemove) {
    return (
      <span
        className="inline-flex items-center rounded bg-ok-bg px-1.5 py-0.5 text-xs font-medium text-ok-text ring-1 ring-inset ring-ok-line"
        title="A superseded empty duplicate with its history captured — safe to remove in Seller Central"
      >
        Ready
      </span>
    );
  }
  return <span className="text-muted">—</span>;
}
